import type { ComponentType, ReactNode } from "react";
import { cn } from "@/shared/lib/utils.js";

export interface RailItem {
  icon: ComponentType<{ className?: string }>;
  label: string;
  active?: boolean;
  onClick?: () => void;
}

/** Botón del riel lateral (negro, solo desktop). Activo = chip de acento de marca. */
export function RailButton({ icon: Icon, label, active, onClick }: RailItem) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-current={active ? "page" : undefined}
      onClick={onClick}
      className={cn(
        "flex size-12 items-center justify-center rounded-xl transition-colors",
        active
          ? "bg-brand-accent text-brand-accent-foreground"
          : "text-background/50 hover:bg-background/10 hover:text-background",
      )}
    >
      <Icon className="size-5" aria-hidden />
    </button>
  );
}

/**
 * Marco de aplicación a pantalla completa: fondo papel + cuadrícula blueprint, riel
 * de iconos negro a la izquierda (solo desktop) y columna de contenido scrolleable.
 * Cada página renderiza su propio header/hero dentro de `children`.
 */
export function AppShell({
  nav,
  footer,
  children,
}: {
  nav: RailItem[];
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="paper-surface paper-grid relative flex h-screen flex-col overflow-hidden md:flex-row">
      <div aria-hidden className="absolute inset-x-0 top-0 z-30 h-1 bg-brand-accent" />

      <nav className="relative z-20 hidden w-20 shrink-0 flex-col items-center gap-6 border-r border-border bg-foreground py-8 md:flex">
        {nav.map((item) => (
          <RailButton key={item.label} {...item} />
        ))}
        {footer && <div className="mt-auto flex flex-col items-center gap-5">{footer}</div>}
      </nav>

      <div className="flex min-w-0 flex-1 flex-col overflow-y-auto">{children}</div>
    </div>
  );
}
